import FeatureInfo from 'components/FeatureInfo'
import StaticPageLayout from 'components/StaticPageLayout'
import EmbeddedVideo from 'components/features-page/EmbeddedVideo'
import FeatureHeader from 'components/features-page/FeatureHeader'
import FeatureItem from 'components/features-page/FeatureItem'
import FeatureSummary from 'components/features-page/FeatureSummary'
import { graphql, useStaticQuery } from 'gatsby'
import Garry1 from 'images/features/garry-1.svg'
import Garry2 from 'images/features/garry-2.svg'
import Garry3 from 'images/features/garry-3.svg'
import Garry4 from 'images/features/garry-4.svg'
import Garry5 from 'images/features/garry-5.svg'
import Garry6 from 'images/features/garry-6.svg'
import { ReactElement } from 'react'
import { Flex, Grid } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import { useFeaturePageSlug } from 'utils/useFeaturePageSlug'

interface GridFeatureItem {
  title: string
  description: string
}

export interface AboutTheFeature {
  title: string
  items: string[]
}

const featureIcons: React.ElementType[] = [Garry1, Garry2, Garry3, Garry4, Garry5, Garry6]

export default function FeaturePageSkeleton({
  title,
  subtitle,
  featureImage,
  summaryText,
  gridItems,
  cta,
  aboutTheFeature,
  videoTitle,
  videoSrc,
  children,
  metaDescription,
  metaKeywords,
  midSection,
}: {
  title: string
  subtitle: string
  featureImage?: string
  summaryText?: string
  gridItems: GridFeatureItem[]
  cta: ReactElement
  aboutTheFeature: AboutTheFeature[]
  videoTitle?: string
  videoSrc?: string
  children?: ReactElement
  metaDescription: string
  metaKeywords: string[]
  midSection?: ReactElement
}): ReactElement {
  const data = useStaticQuery<Queries.FeaturePageSkeletonQuery>(graphql`
    query FeaturePageSkeleton {
      directus {
        general_translations {
          strings {
            languages_code {
              code
            }
            show_more
            show_less
            summary
            by_the_way
          }
        }
      }
    }
  `)

  const generalTranslations = getEnglishStrings(data.directus.general_translations?.strings)
  const queryParam = useFeaturePageSlug()
  return (
    <StaticPageLayout
      title={`${title} | Windscribe`}
      metaDescription={metaDescription}
      metaKeywords={metaKeywords}
      ctaQueryParams={queryParam}
    >
      <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: 'default', mb: 'default' }}>
        <FeatureHeader title={title} subtitle={subtitle} imageSource={featureImage} />
        {summaryText && (
          <FeatureSummary
            text={summaryText}
            summaryTranslation={generalTranslations?.summary ?? ''}
          />
        )}
        {videoSrc && <EmbeddedVideo videoTitle={videoTitle} videoSrc={videoSrc} />}
        {midSection}
        <GridItems gridItems={gridItems} cta={cta} />
        <AboutTheFeature aboutTheFeature={aboutTheFeature} />
        {children}
      </Flex>
    </StaticPageLayout>
  )
}

export function GridItems({
  gridItems,
  cta,
}: {
  gridItems: GridFeatureItem[]
  cta: ReactElement
}): ReactElement {
  return (
    <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
      <Grid
        columns={[1, 2, 2]}
        sx={{
          gap: ['3.2rem', '4.8rem 1.6rem', '4.8rem 8rem'],
          maxWidth: ['100%', 'inherit', 'maxContentWidth'],
          width: ['inherit', '100%', 'inherit'],
        }}
      >
        {gridItems?.map((item, index) => (
          <FeatureItem
            key={item.title}
            icon={featureIcons[index]}
            title={item.title}
            description={item.description}
          />
        ))}
        {gridItems.length % 2 !== 0 && (
          <Flex sx={{ '& > *': { height: 'fit-content' }, margin: '0 auto', alignItems: 'center' }}>
            {cta}
          </Flex>
        )}
      </Grid>
      {gridItems.length % 2 === 0 && (
        <Flex sx={{ mt: ['3.2rem', '4.8rem'] }}>
          <Flex sx={{ flexGrow: 0 }}>{cta}</Flex>
        </Flex>
      )}
    </Flex>
  )
}

export function AboutTheFeature({
  aboutTheFeature,
}: {
  aboutTheFeature: AboutTheFeature[]
}): ReactElement {
  return (
    <>
      {aboutTheFeature.map(feature => (
        <FeatureInfo feature={feature} key={feature.title} />
      ))}
    </>
  )
}
